import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import './PromoTrialPopup.css';
import appIcon from '../../assets/logo_white_bg.png';
import googlePlayBadge from '../../assets/google-play-badge.png';
import appStoreBadge from '../../assets/app-store-badge.png';
import PropertyCashbackOfferBanner, {
  hasCashbackBannerBeenShown,
} from './PropertyCashbackOfferBanner';

const APP_FEATURES = [
  'List & manage properties with exact location',
  'Pinpoint coordinates for accurate property mapping',
  'Share property location instantly with buyers',
  'Boost visibility for rent, sale & resale listings',
  'Easy property posting with smart tools',
];

const PromoTrialPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showCashbackBanner, setShowCashbackBanner] = useState(false);
  const location = useLocation();
  const timeoutRef = useRef(null);
  const showDelayRef = useRef(null);
  const downloadPopupWasShownRef = useRef(false);
  const SESSION_SHOWN_KEY = '360coordinates_popup_shown';
  const DOWNLOAD_PAGE_SHOWN_KEY = '360coordinates_download_popup_shown';

  const isDownloadPage = location.pathname.toLowerCase() === '/downloadapp';

  const hasBeenShownInSession = () => {
    if (isDownloadPage) {
      return sessionStorage.getItem(DOWNLOAD_PAGE_SHOWN_KEY) === 'true';
    }
    return sessionStorage.getItem(SESSION_SHOWN_KEY) === 'true';
  };

  const markAsShownInSession = () => {
    if (isDownloadPage) {
      sessionStorage.setItem(DOWNLOAD_PAGE_SHOWN_KEY, 'true');
    }
    sessionStorage.setItem(SESSION_SHOWN_KEY, 'true');
  };

  const shouldShowOnRoute = () => {
    const path = location.pathname.toLowerCase();

    if (path === '/login' || path === '/register') {
      return false;
    }

    // Show on download page as well (promotes app install when user lands here)
    if (path.includes('/details/') || path.includes('/viewdetails')) {
      return false;
    }

    // Logged-in dashboard areas where popup would interrupt core workflows
    const dashboardExactPaths = new Set([
      '/buyerprofile',
      '/chatus',
      '/chat',
      '/searchresults',
      '/seller-dashboard',
    ]);
    if (dashboardExactPaths.has(path)) {
      return false;
    }

    const dashboardPrefixes = [
      '/seller/properties',
      '/seller/inquiries',
      '/agent/',
      '/upcoming-project/',
    ];
    if (dashboardPrefixes.some((prefix) => path.startsWith(prefix))) {
      return false;
    }

    return true;
  };

  const openCashbackBannerAfterClose = () => {
    if (!downloadPopupWasShownRef.current || hasCashbackBannerBeenShown()) {
      return;
    }
    setTimeout(() => {
      setShowCashbackBanner(true);
    }, 280);
  };

  // Manual close handler - just closes without disabling
  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      setIsVisible(false);
      openCashbackBannerAfterClose();
    }, 250); // Match CSS animation duration
    
    // Clear timeout if still running
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const PLAY_STORE_URL =
    'https://play.google.com/store/apps/details?id=com.indiapropertys.app360';
  const APP_STORE_URL =
    'https://apps.apple.com/in/app/360coordinates/id6768521931';

  const handlePlayStoreClick = () => {
    window.open(PLAY_STORE_URL, '_blank', 'noopener,noreferrer');
    handleClose();
  };

  const handleAppStoreClick = () => {
    window.open(APP_STORE_URL, '_blank', 'noopener,noreferrer');
    handleClose();
  };

  useEffect(() => {
    // Legacy: store buttons used to disable the popup forever — clear so it can show again
    localStorage.removeItem('360coordinates_popup_disabled');

    // Don't show if popup has already been shown in this session
    if (hasBeenShownInSession()) {
      return;
    }

    // Check if should show on current route
    if (!shouldShowOnRoute()) {
      return;
    }

    // Clear any existing timeouts
    if (showDelayRef.current) {
      clearTimeout(showDelayRef.current);
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Small delay to ensure smooth animation on page load
    showDelayRef.current = setTimeout(() => {
      setIsAnimating(true);
      setIsVisible(true);
      downloadPopupWasShownRef.current = true;
      
      // Mark as shown in this session so it won't appear again
      markAsShownInSession();
      
      // Popup will remain visible until user clicks download or close button
    }, isDownloadPage ? 150 : 300);

    // Cleanup on unmount or route change
    return () => {
      if (showDelayRef.current) {
        clearTimeout(showDelayRef.current);
        showDelayRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [location.pathname]); // Re-check when route changes

  if (!isVisible && !showCashbackBanner) {
    return null;
  }

  return (
    <>
      {isVisible && (
    <div className={`promo-trial-popup-overlay ${isAnimating ? 'promo-trial-popup-visible' : 'promo-trial-popup-hidden'}`}>
      <div className="promo-trial-popup-container">
        <button
          type="button"
          className="promo-trial-popup-close"
          onClick={handleClose}
          aria-label="Close popup"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <aside className="promo-trial-popup-brand">
          <img className="promo-trial-popup-app-icon" src={appIcon} alt="360Coordinates App" />
          <span className="promo-trial-popup-brand-name">360Coordinates</span>
        </aside>

        <div className="promo-trial-popup-main">
          <h2 className="promo-trial-popup-title">Download the app</h2>
          <p className="promo-trial-popup-subtitle">List, search and share properties with exact map locations</p>

          <ul className="promo-trial-popup-features">
            {APP_FEATURES.map((feature) => (
              <li key={feature} className="promo-trial-popup-feature">
                <span className="promo-trial-popup-feature-icon" aria-hidden>✓</span>
                <span>{feature}</span>
              </li>
            ))}
          </ul>

          <div className="promo-trial-popup-store-buttons">
            <button
              type="button"
              className="promo-trial-popup-store-badge-btn promo-trial-popup-store-badge-btn--google"
              onClick={handlePlayStoreClick}
              aria-label="Get it on Google Play"
            >
              <span className="promo-trial-popup-store-badge-wrap promo-trial-popup-store-badge-wrap--google">
                <img
                  src={googlePlayBadge}
                  alt="Get it on Google Play"
                  className="promo-trial-popup-store-badge promo-trial-popup-store-badge--google"
                />
              </span>
            </button>

            <button
              type="button"
              className="promo-trial-popup-store-badge-btn promo-trial-popup-store-badge-btn--apple"
              onClick={handleAppStoreClick}
              aria-label="Download on the App Store"
            >
              <span className="promo-trial-popup-store-badge-wrap promo-trial-popup-store-badge-wrap--apple">
                <img
                  src={appStoreBadge}
                  alt="Download on the App Store"
                  className="promo-trial-popup-store-badge promo-trial-popup-store-badge--apple"
                />
              </span>
            </button>
          </div>

          <p className="promo-trial-popup-limited">No signup required to download</p>
        </div>
      </div>
    </div>
      )}

      <PropertyCashbackOfferBanner
        isOpen={showCashbackBanner}
        onClose={() => setShowCashbackBanner(false)}
      />
    </>
  );
};

export default PromoTrialPopup;